
import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Float, MeshDistortMaterial } from "@react-three/drei";
import * as THREE from "three";

const AIBall = ({ isTalking }: { isTalking: boolean }) => {
  const ballRef = useRef<THREE.Group>(null);
  const coreRef = useRef<THREE.Mesh>(null);
  
  // Animate the AI ball
  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    
    // Base rotation
    if (ballRef.current) {
      ballRef.current.rotation.y = time * 0.2;
    }
    
    // Core pulsing when talking
    if (coreRef.current) {
      if (isTalking) {
        // More energetic pulsing when talking
        const pulseScale = 0.8 + Math.sin(time * 8) * 0.1 + Math.sin(time * 15) * 0.05;
        coreRef.current.scale.set(pulseScale, pulseScale, pulseScale);
        
        // Brighter when talking
        (coreRef.current.material as THREE.MeshStandardMaterial).emissiveIntensity = 
          1.5 + Math.sin(time * 10) * 0.5;
      } else {
        // Gentle pulsing when idle
        const idlePulse = 0.75 + Math.sin(time * 2) * 0.05;
        coreRef.current.scale.set(idlePulse, idlePulse, idlePulse);
        
        // Dimmer when idle
        (coreRef.current.material as THREE.MeshStandardMaterial).emissiveIntensity = 
          1.0 + Math.sin(time * 3) * 0.2;
      }
    }
  });

  // Create the energy particles inside the ball
  const renderEnergyParticles = () => {
    return [...Array(20)].map((_, i) => {
      // Position particles in a spherical pattern inside the ball
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const r = 0.5 + Math.random() * 0.2;
      const x = r * Math.sin(phi) * Math.cos(theta);
      const y = r * Math.sin(phi) * Math.sin(theta);
      const z = r * Math.cos(phi);
      
      return (
        <mesh key={i} position={[x, y, z]}>
          <sphereGeometry args={[0.02 + Math.random() * 0.03, 8, 8]} />
          <meshStandardMaterial 
            color="#ffffff" 
            emissive="#88ccff"
            emissiveIntensity={2}
          />
        </mesh>
      );
    });
  };

  // Optional energy waves when talking
  const renderEnergyWaves = () => {
    if (!isTalking) return null;
    
    return (
      <mesh>
        <sphereGeometry args={[1.1, 32, 32]} />
        <MeshDistortMaterial
          color="#4488ff"
          transparent={true}
          opacity={0.4}
          speed={5}
          distort={0.4}
        />
      </mesh>
    );
  };

  return (
    <Float rotationIntensity={0.2} floatIntensity={0.5} speed={1.5}>
      <group ref={ballRef}>
        {/* Outer translucent shell */}
        <mesh>
          <sphereGeometry args={[1, 64, 64]} />
          <meshPhysicalMaterial 
            color="#88ccff"
            transparent={true}
            opacity={0.3}
            roughness={0.1}
            metalness={0.9}
            clearcoat={1.0}
            clearcoatRoughness={0.1}
            transmission={0.98}
            ior={1.5}
          />
        </mesh>
        
        {/* Inner energetic core */}
        <mesh ref={coreRef}>
          <sphereGeometry args={[0.7, 32, 32]} />
          <meshStandardMaterial
            color="#4488ff"
            emissive="#0055ff"
            emissiveIntensity={1.0}
            roughness={0.2}
            metalness={0.8}
          />
        </mesh>
        
        {/* Energy particles inside (small spheres) */}
        {renderEnergyParticles()}
        
        {/* Dynamic energy waves when talking */}
        {renderEnergyWaves()}
      </group>
    </Float>
  );
};

export default AIBall;
